import { PayloadAction } from '@reduxjs/toolkit';

import { VariantsState } from 'editor/src/store/variants/types';

export interface ResolvePlaceholderUidPayload {
  oldProductUid: string;
  newProductUid: string;
}

/**
 * Validates that a product UID has a safe format.
 * Accepts alphanumeric characters, underscores, hyphens, and colons (for placeholder UIDs).
 */
const isValidProductUid = (uid: string): boolean => {
  if (!uid || typeof uid !== 'string') {
    return false;
  }
  // Allow alphanumeric, underscores, hyphens, and colons (used in placeholder UIDs)
  const PRODUCT_UID_PATTERN = /^[a-zA-Z0-9_:-]+$/;
  return PRODUCT_UID_PATTERN.test(uid) && uid.length <= 500;
};

/**
 * This reducer performs a surgical update of a placeholder product UID to the real product UID.
 * It updates all relevant parts of the variants state without resetting the entire product tab.
 */
const resolvePlaceholderUidReducer = (state: VariantsState, action: PayloadAction<ResolvePlaceholderUidPayload>) => {
  const { oldProductUid, newProductUid } = action.payload;

  // Validate inputs - early return if UIDs are missing or invalid
  if (!isValidProductUid(oldProductUid) || !isValidProductUid(newProductUid)) {
    return;
  }

  // 1. Update productVariations
  const variationIndex = state.product.productVariations.findIndex((v) => v.productUid === oldProductUid);
  if (variationIndex !== -1) {
    state.product.productVariations[variationIndex].productUid = newProductUid;
  }

  // 2. Update variationGroups - find any variation info that has the placeholder UID
  state.variationGroups.forEach((group) => {
    group.variationsInfo.forEach((info) => {
      if (info.variation.productUid === oldProductUid) {
        info.variation.productUid = newProductUid;
      }
    });
  });

  // 3. Update outOfStock if it contains the placeholder
  if (state.product.outOfStock[oldProductUid] !== undefined) {
    state.product.outOfStock[newProductUid] = state.product.outOfStock[oldProductUid];
    delete state.product.outOfStock[oldProductUid];
  }

  // 4. Update unavailableProducts if it contains the placeholder
  if (state.product.unavailableProducts[oldProductUid] !== undefined) {
    state.product.unavailableProducts[newProductUid] = state.product.unavailableProducts[oldProductUid];
    delete state.product.unavailableProducts[oldProductUid];
  }

  // 5. Update selectedProductUid if it was the placeholder
  if (state.selectedProductUid === oldProductUid) {
    state.selectedProductUid = newProductUid;
  }

  // 6. Update layoutPerProductUids if it contains the placeholder
  if (state.layoutPerProductUids[oldProductUid] !== undefined) {
    state.layoutPerProductUids[newProductUid] = state.layoutPerProductUids[oldProductUid];
    delete state.layoutPerProductUids[oldProductUid];
  }

  // 7. Update groupedSpreadsPerProductUids if it contains the placeholder
  if (state.groupedSpreadsPerProductUids[oldProductUid] !== undefined) {
    state.groupedSpreadsPerProductUids[newProductUid] = state.groupedSpreadsPerProductUids[oldProductUid];
    delete state.groupedSpreadsPerProductUids[oldProductUid];
  }

  // 8. Update resizableElementPerProductUids if it contains the placeholder
  if (state.resizableElementPerProductUids?.[oldProductUid] !== undefined) {
    state.resizableElementPerProductUids[newProductUid] = state.resizableElementPerProductUids[oldProductUid];
    delete state.resizableElementPerProductUids[oldProductUid];
  }
};

export default resolvePlaceholderUidReducer;
